import type { AlertRule, Incident, Service, RCACause } from "@coremonit/types";
import { makeRng, int, pick, chance } from "./rng";

export function generateRules(services: Service[], seed = 42): AlertRule[] {
  const rng = makeRng(seed);
  const RULES: Array<{ name: string; metric: string; op: AlertRule["operator"]; th: number; sev: AlertRule["severity"] }> = [
    { name: "High p95 latency",    metric: "latency.p95",   op: ">", th: 500, sev: "high" },
    { name: "Error rate > 3%",     metric: "err.rate",      op: ">", th: 3,   sev: "critical" },
    { name: "CPU > 90%",           metric: "cpu.util",      op: ">", th: 90,  sev: "high" },
    { name: "Memory > 90%",        metric: "mem.util",      op: ">", th: 90,  sev: "high" },
    { name: "Restart loop",        metric: "pod.restarts",  op: ">", th: 5,   sev: "high" },
    { name: "OOM kills",           metric: "pod.oomkills",  op: ">", th: 0,   sev: "critical" },
    { name: "Disk near full",      metric: "disk.util",     op: ">", th: 88,  sev: "medium" },
    { name: "Traffic drop",        metric: "req.rate",      op: "<", th: 30,  sev: "medium" },
    { name: "Dependency failure",  metric: "dep.err",       op: ">", th: 5,   sev: "high" },
    { name: "Business KPI drift",  metric: "biz.orders",    op: "<", th: 100, sev: "medium" },
  ];
  return RULES.map((r, i) => ({
    id: `rule_${i}`,
    name: r.name,
    metric: r.metric,
    operator: r.op,
    threshold: r.th,
    window: pick(rng, ["5m", "15m", "1h"]),
    severity: r.sev,
    serviceIds: services.filter(() => chance(rng, 30)).map((s) => s.id),
    enabled: chance(rng, 85),
    routes: [
      { channel: "slack",     target: pick(rng, ["#alerts-prod", "#sre-oncall", "#incident-response"]) },
      { channel: "pagerduty", target: pick(rng, ["team-payments", "team-platform", "team-search"]) },
    ],
  }));
}

export function generateIncidents(services: Service[], seed = 42): Incident[] {
  const rng = makeRng(seed);
  const bad = services.filter((s) => s.status !== "healthy");
  return bad.slice(0, 12).map((s, i): Incident => ({
    id: `inc_${i}`,
    title: `${s.status === "critical" ? "🔴" : "🟠"} ${s.name}: ${s.errorRatePct.toFixed(1)}% error rate, p95=${s.latencyP95Ms}ms`,
    serviceId: s.id,
    serviceName: s.name,
    severity: s.status === "critical" ? "critical" : "high",
    status: chance(rng, 65) ? "firing" : chance(rng, 60) ? "acknowledged" : "resolved",
    startedAt: new Date(Date.now() - int(rng, 5, 240) * 60000).toISOString(),
    resolvedAt: chance(rng, 30) ? new Date(Date.now() - int(rng, 1, 30) * 60000).toISOString() : undefined,
    ruleName: pick(rng, ["High p95 latency", "Error rate > 3%", "Restart loop", "Memory > 90%"]),
    description: `Rule triggered on ${s.name} — investigating recent deploys, upstream calls, and infrastructure changes.`,
  }));
}

export function generateRCA(service: Service, seed = 42): RCACause[] {
  const rng = makeRng(seed + service.id.length);
  return [
    {
      rank: 1,
      title: `Recent deploy v${service.version} introduced a slow SQL join in checkout path`,
      confidencePct: 87,
      category: "deployment",
      evidence: [
        "p99 latency jumped from 320ms → 1,820ms exactly at deploy time",
        "Slow SQL log shows new query on order_lines LEFT JOIN inventory (avg 640ms)",
        "Feature flag FEATURE_ASYNC_CHECKOUT enabled in this revision",
      ],
      linkedMetrics: ["latency.p99", "db.query.duration"],
      linkedTraces: ["tr_recent_1", "tr_recent_2"],
      linkedLogs: [],
      linkedDeployments: [`dep_${service.id}_latest`],
    },
    {
      rank: 2,
      title: "Downstream inventory-service saturating its connection pool",
      confidencePct: 71,
      category: "dependency",
      evidence: [
        "inventory-service connection pool at 96% capacity",
        "Wait time on getConnection() elevated across all callers",
      ],
      linkedMetrics: ["conn.active"],
      linkedTraces: ["tr_dep_wait_9"],
      linkedLogs: ["log_conn_pool_87"],
      linkedDeployments: [],
    },
    {
      rank: 3,
      title: "Traffic spike from mobile-bff (+220% RPM vs baseline)",
      confidencePct: 54,
      category: "traffic",
      evidence: [
        "mobile-bff RPM went from 380 → 1,220 over 15 minutes",
        "Correlates with a push-notification campaign that started at 14:22",
      ],
      linkedMetrics: ["req.rate"],
      linkedTraces: [],
      linkedLogs: [],
      linkedDeployments: [],
    },
    {
      rank: 4,
      title: "Node ip-10-42-11-31 reporting elevated packet loss",
      confidencePct: 33,
      category: "network",
      evidence: [
        "3/8 replicas of the service are scheduled on the affected node",
        "Kubelet reports NodeNotReady briefly at 14:31",
      ],
      linkedMetrics: ["net.mbps"],
      linkedTraces: [],
      linkedLogs: ["log_node_pressure_12"],
      linkedDeployments: [],
    },
  ];
}
